import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../../context/SiteContentContext';

const FreeTrialClass = () => {
  const { siteContent } = useSiteContent();
  const section = siteContent?.freeTrialClass || {};
  
  if (section.visible === false) return null;

  const title = section.title || 'Experience English Passion Before You Enroll';
  const subtitle = section.subtitle || '';
  const benefits = section.benefits || [];
  const steps = section.steps || [];
  const ctaText = section.ctaText || 'Book Your Free Trial Class Now';
  const benefitsTitle = section.benefitsTitle || 'Benefits of a Free Trial Class';
  const stepsTitle = section.stepsTitle || 'Easy Registration';
  const ctaSubtitle = section.ctaSubtitle || 'Take the first step toward improving your English skills and building your confidence.';

  return (
    <section className="py-16 sm:py-20 md:py-24 bg-white" data-testid="free-trial-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
            {title}
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-slate-50 rounded-2xl p-6 md:p-8"
          >
            <h3 className="text-xl font-semibold text-slate-900 mb-6">
              {benefitsTitle}
            </h3>
            <ul className="space-y-4">
              {benefits.map((benefit, index) => {
                const benefitText = typeof benefit === 'string' ? benefit : benefit;
                return (
                  <li key={`benefit-${index}`} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{benefitText}</span>
                  </li>
                );
              })}
            </ul>
          </motion.div>

          {/* Steps */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-amber-50 rounded-2xl p-6 md:p-8"
          >
            <h3 className="text-xl font-semibold text-slate-900 mb-6">
              {stepsTitle}
            </h3>
            <ol className="space-y-4">
              {steps.map((step, index) => {
                const stepText = typeof step === 'string' ? step : step;
                return (
                  <li key={`step-${index}`} className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-amber-400 text-amber-900 rounded-full flex items-center justify-center font-semibold text-sm">
                      {index + 1}
                    </span>
                    <span className="text-slate-700 pt-1">{stepText}</span>
                  </li>
                );
              })}
            </ol>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mt-12"
        >
          <p className="text-slate-600 mb-6">
            {ctaSubtitle}
          </p>
          <Link
            to="/contact"
            data-testid="free-trial-cta"
            className="inline-flex items-center justify-center bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4 font-medium transition-all text-lg"
          >
            {ctaText}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FreeTrialClass;
